// ResourceUploadWizard
//
// Wizard unificado para subir recursos de Formacion (videos o PDFs).
// Sirve a las dos verticales: recibe `api` (cliente HTTP) y `audienceOptions`
// (a quien puede dirigirse el recurso), asi que el mismo componente vale para
// REVI (terapeuta / institucion) y para K12 (profesor / institucion).
//
// Pasos:
//   1. Tipo (Video o PDF)
//   2. Datos (titulo, descripcion y audiencia)
//   3. Contenido (URL si video / fichero PDF si pdf)
//   4. Imagen miniatura/portada (opcional)
//   5. Resumen + Guardar -> Result success
//
// FIX 2026-08-04 (bloqueo reportado por soporte): el boton "Siguiente" se
// quedaba deshabilitado aunque el titulo y la descripcion estuvieran
// rellenos. La causa era que `canGoNext()` leia los valores con
// `form.getFieldsValue()` durante el render, y escribir en un Input de AntD
// NO provoca re-render del componente padre: el footer se pintaba una sola vez
// con los campos vacios y ya no se volvia a evaluar. Ahora los valores viven en
// un estado propio alimentado por `onValuesChange`, de modo que cada pulsacion
// re-renderiza y el boton se habilita en cuanto los datos son validos.

import React, { useCallback, useMemo, useState } from "react";
import {
  Modal,
  Steps,
  Form,
  Input,
  Radio,
  Upload,
  Button,
  Alert,
  Checkbox,
  Result,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  PlayCircleOutlined,
  FilePdfOutlined,
  InboxOutlined,
  PictureOutlined,
  CopyOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import styles from "./ResourceUploadWizard.module.scss";

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const MAX_PDF_MB = 25;
const MAX_IMAGE_MB = 5;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

function isLikelyYouTubeOrVimeo(url) {
  if (!url) return false;
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}

const STEPS = [
  { title: "Tipo" },
  { title: "Datos" },
  { title: "Contenido" },
  { title: "Imagen" },
  { title: "Guardar" },
];

export function ResourceUploadWizard({
  open,
  accessToken,
  api,
  onClose,
  onCreated,
  initialType,
  audienceOptions = [],
  defaultAudiences = [],
  audienceLabel = "¿Quién debe verlo?",
}) {
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();

  const initialValues = useMemo(
    () => ({
      title: "",
      description: "",
      url: "",
      audiences: defaultAudiences.length
        ? defaultAudiences
        : audienceOptions.slice(0, 1).map((o) => o.value),
    }),
    [audienceOptions, defaultAudiences],
  );

  // Espejo de los valores del Form en estado propio: es lo que permite que el
  // footer se re-evalue mientras el usuario escribe (ver FIX en la cabecera).
  const [values, setValues] = useState(initialValues);

  const [type, setType] = useState(initialType || "video");
  const [documentFile, setDocumentFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const reset = useCallback(() => {
    setStep(0);
    setType(initialType || "video");
    setDocumentFile(null);
    setImageFile(null);
    setImagePreview(null);
    setSaving(false);
    setError(null);
    setResult(null);
    form.resetFields();
    setValues(initialValues);
  }, [form, initialType, initialValues]);

  const close = () => {
    if (saving) {
      message.warning("Espera a que termine la subida");
      return;
    }
    onClose();
    setTimeout(reset, 250);
  };

  const handlePickDocument = (picked) => {
    if (picked.type !== "application/pdf") {
      message.error("Solo se permiten ficheros PDF");
      return false;
    }
    if (picked.size > MAX_PDF_MB * 1024 * 1024) {
      message.error(`El PDF supera ${MAX_PDF_MB} MB`);
      return false;
    }
    setDocumentFile(picked);
    return false; // evita upload automatico
  };

  const handlePickImage = (picked) => {
    if (!ALLOWED_IMAGE_TYPES.includes(picked.type)) {
      message.error("Imagen no permitida (jpg/png/webp/gif)");
      return false;
    }
    if (picked.size > MAX_IMAGE_MB * 1024 * 1024) {
      message.error(`La imagen supera ${MAX_IMAGE_MB} MB`);
      return false;
    }
    setImageFile(picked);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(picked);
    return false;
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        type,
        title: values.title?.trim(),
        description: values.description?.trim(),
        // Multipart no transporta arrays: el backend acepta CSV.
        audiences: (values.audiences || []).join(","),
      };
      if (type === "video") {
        payload.url = values.url?.trim();
      }
      if (imageFile) payload.image = imageFile;
      if (type === "pdf" && documentFile) payload.document = documentFile;

      const created = await api.create(accessToken, payload);
      setResult(created.resource || created.data || null);
      onCreated?.(created.resource || created.data);
    } catch (e) {
      setError(e?.body?.message || e?.message || "Error subiendo el recurso");
    } finally {
      setSaving(false);
    }
  };

  const canGoNext = () => {
    if (step === 0) return !!type;
    if (step === 1) {
      return (
        (values.title || "").trim().length > 2 &&
        (values.description || "").trim().length > 2 &&
        (values.audiences || []).length > 0
      );
    }
    if (step === 2) {
      if (type === "video") return !!(values.url || "").trim();
      return !!documentFile;
    }
    return true;
  };

  const next = async () => {
    if (step === 1) {
      try {
        await form.validateFields(["title", "description", "audiences"]);
      } catch (e) {
        return;
      }
    }
    if (step === 2 && type === "video") {
      try {
        await form.validateFields(["url"]);
      } catch (e) {
        return;
      }
    }
    if (step === 4) {
      submit();
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const copyResultUrl = () => {
    if (!result) return;
    const url = result.type === "video" ? result.url : result.file;
    if (!url) {
      message.warning("Este recurso no tiene URL externa todavia");
      return;
    }
    try {
      navigator.clipboard.writeText(url);
      message.success("URL copiada");
    } catch (e) {
      message.error("No se pudo copiar");
    }
  };

  const TypeIcon = type === "video" ? PlayCircleOutlined : FilePdfOutlined;

  const audienceLabels = (values.audiences || []).map(
    (v) => audienceOptions.find((o) => o.value === v)?.label || v,
  );

  // Cada paso se oculta con CSS en vez de desmontarse: asi los campos del Form
  // siguen montados y `validateFields` puede comprobarlos en cualquier momento.
  const stepStyle = (index) => ({
    display: step === index ? "block" : "none",
  });

  return (
    <Modal
      title={
        <span className={styles.title}>
          <CloudUploadOutlined /> Nuevo recurso de Formación
        </span>
      }
      open={open}
      onCancel={close}
      width={720}
      destroyOnClose
      footer={
        result ? (
          <Space>
            <Button onClick={close}>Cerrar</Button>
            <Button
              type="primary"
              onClick={reset}
              icon={<CloudUploadOutlined />}
            >
              Subir otro
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={close} disabled={saving}>
              Cancelar
            </Button>
            {step > 0 ? (
              <Button onClick={prev} disabled={saving}>
                Atrás
              </Button>
            ) : null}
            <Button
              type="primary"
              onClick={next}
              disabled={!canGoNext() || saving}
              loading={saving && step === 4}
            >
              {step === 4 ? "Guardar" : "Siguiente"}
            </Button>
          </Space>
        )
      }
    >
      {!result ? (
        <Steps
          current={step}
          size="small"
          items={STEPS}
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo guardar"
          description={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      ) : null}

      {result ? (
        <Result
          status="success"
          title="Recurso creado"
          subTitle={`${result.title} ya esta disponible en el portal.`}
          extra={[
            <Button
              key="copy"
              type="primary"
              icon={<CopyOutlined />}
              onClick={copyResultUrl}
            >
              Copiar URL
            </Button>,
          ]}
        />
      ) : null}

      {!result ? (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={initialValues}
          onValuesChange={(_changed, all) => setValues(all)}
        >
          {/* Step 0: Tipo */}
          <div style={stepStyle(0)}>
            <div className={styles.typePicker}>
              <Radio.Group
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={{ width: "100%" }}
              >
                <div className={styles.typeOptions}>
                  <Radio.Button value="video" className={styles.typeBtn}>
                    <div className={styles.typeBtnInner}>
                      <PlayCircleOutlined className={styles.typeIcon} />
                      <span className={styles.typeName}>Vídeo</span>
                      <span className={styles.typeHint}>
                        URL externa de YouTube o Vimeo
                      </span>
                    </div>
                  </Radio.Button>
                  <Radio.Button value="pdf" className={styles.typeBtn}>
                    <div className={styles.typeBtnInner}>
                      <FilePdfOutlined className={styles.typeIcon} />
                      <span className={styles.typeName}>Guía PDF</span>
                      <span className={styles.typeHint}>
                        Subida de fichero PDF (max {MAX_PDF_MB} MB)
                      </span>
                    </div>
                  </Radio.Button>
                </div>
              </Radio.Group>
            </div>
          </div>

          {/* Step 1: Datos */}
          <div style={stepStyle(1)}>
            <Form.Item
              name="title"
              label="Título"
              rules={[
                { required: true, message: "Pon un título" },
                { min: 3, message: "Mínimo 3 caracteres" },
                { max: 120 },
              ]}
            >
              <Input placeholder="Ej. Registro de terapeutas" maxLength={120} />
            </Form.Item>
            <Form.Item
              name="description"
              label="Descripción"
              rules={[
                { required: true, message: "Pon una descripción" },
                { min: 3 },
                { max: 600 },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Resume brevemente de qué trata. Lo verá quien entre al portal."
                maxLength={600}
                showCount
              />
            </Form.Item>

            {audienceOptions.length ? (
              <Form.Item
                name="audiences"
                label={audienceLabel}
                rules={[
                  {
                    required: true,
                    message: "Marca al menos un destinatario",
                  },
                ]}
                extra="Puedes marcar los dos si el recurso sirve para ambos perfiles."
              >
                <Checkbox.Group options={audienceOptions} />
              </Form.Item>
            ) : null}
          </div>

          {/* Step 2: Contenido */}
          <div style={stepStyle(2)}>
            {type === "video" ? (
              <>
                <Form.Item
                  name="url"
                  label="URL del vídeo (YouTube o Vimeo)"
                  rules={[
                    { required: true, message: "Indica la URL del vídeo" },
                    { type: "url", message: "URL no válida" },
                  ]}
                  extra="El portal sólo embebe YouTube/Vimeo. Otras URLs se abrirán en nueva pestaña."
                >
                  <Input
                    placeholder="https://www.youtube.com/watch?v=..."
                    prefix={<PlayCircleOutlined />}
                  />
                </Form.Item>
                {values.url && !isLikelyYouTubeOrVimeo(values.url) ? (
                  <Alert
                    type="warning"
                    message="La URL no parece de YouTube ni Vimeo"
                    description="No se podrá previsualizar dentro del portal, solo abrir en nueva pestaña."
                    showIcon
                  />
                ) : null}
              </>
            ) : (
              <div>
                <Dragger
                  multiple={false}
                  showUploadList={false}
                  beforeUpload={handlePickDocument}
                  accept="application/pdf"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Arrastra el PDF o haz click para seleccionarlo
                  </p>
                  <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                    Máximo {MAX_PDF_MB} MB. Solo .pdf
                  </p>
                </Dragger>
                {documentFile ? (
                  <div className={styles.filePreview}>
                    <FilePdfOutlined className={styles.filePreviewIcon} />
                    <div className={styles.filePreviewMeta}>
                      <div className={styles.filePreviewName}>
                        {documentFile.name}
                      </div>
                      <div className={styles.filePreviewSize}>
                        {(documentFile.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Step 3: Imagen */}
          <div style={stepStyle(3)}>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              {type === "video"
                ? "La miniatura aparece en el carrusel del portal. Opcional."
                : "La portada aparece en el carrusel del portal. Opcional."}
            </Paragraph>
            <Dragger
              multiple={false}
              showUploadList={false}
              beforeUpload={handlePickImage}
              accept={ALLOWED_IMAGE_TYPES.join(",")}
              style={{ minHeight: 140 }}
            >
              {imagePreview ? (
                <div className={styles.imageSelected}>
                  <img
                    src={imagePreview}
                    alt="preview"
                    className={styles.imageSelectedPreview}
                  />
                  <span style={{ marginTop: 8, display: "block" }}>
                    Click o arrastra para cambiar
                  </span>
                </div>
              ) : (
                <>
                  <p className="ant-upload-drag-icon">
                    <PictureOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Selecciona o arrastra una imagen
                  </p>
                  <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                    jpg/png/webp/gif · máx {MAX_IMAGE_MB} MB
                  </p>
                </>
              )}
            </Dragger>
          </div>

          {/* Step 4: Resumen */}
          <div style={stepStyle(4)}>
            <div className={styles.summary}>
              <div className={styles.summaryHero}>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="miniatura"
                    className={styles.summaryImg}
                  />
                ) : (
                  <div className={styles.summaryImgPlaceholder}>
                    <TypeIcon style={{ fontSize: 48, opacity: 0.4 }} />
                  </div>
                )}
                <div className={styles.summaryMeta}>
                  <Title level={5} style={{ margin: 0 }}>
                    {values.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {type === "video" ? "Vídeo" : "Guía PDF"}
                  </Text>
                  <div style={{ marginTop: 6 }}>
                    {audienceLabels.map((label) => (
                      <Tag key={label} color="blue" bordered={false}>
                        {label}
                      </Tag>
                    ))}
                  </div>
                  <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                    {values.description}
                  </Paragraph>
                  {type === "video" ? (
                    <Text className={styles.summaryUrl} ellipsis>
                      {values.url}
                    </Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {documentFile?.name} ·{" "}
                      {(documentFile?.size / (1024 * 1024)).toFixed(2)} MB
                    </Text>
                  )}
                </div>
              </div>
              <Alert
                type="info"
                showIcon
                message="Al guardar, el recurso se sube y aparece en el portal en cuanto recarguen la página."
              />
            </div>
          </div>
        </Form>
      ) : null}
    </Modal>
  );
}
